import { CostingTemplatesSidebar } from '@/components/CostingTemplates/CostingTemplatesSidebar';
import { LayoutCard } from '@/components/LayoutCard';
import { PageLayoutCollapsibleSidebar } from '@/components/PageLayoutCollapsibleSidebar';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { useSourcingTemplates } from '@/resources/sourcingTemplate/sourcingTemplateHandler';
import { route } from '@/utils/routes';
import { Trans, t } from '@lingui/macro';
import { useHasPermission } from '@luminovo/auth';
import { formatLongDateTime, formatRelativeTime, getIntercomArticleURL } from '@luminovo/commons';
import {
    DestructiveSecondaryIconButton,
    Flexbox,
    Link,
    NonIdealState,
    PrimaryButton,
    SecondaryIconButton,
    Tag,
    TanStackTable,
    Text,
    Tooltip,
    colorSystem,
    createColumnHelper,
    useConfirmationDialog,
    useNavigate,
    useTanStackTable,
} from '@luminovo/design-system';
import { SourcingTemplateDTO } from '@luminovo/http-client';
import { ContentCopyRounded, Delete, EditRounded, TravelExploreRounded } from '@mui/icons-material';
import { Box } from '@mui/material';

type RowItemType = SourcingTemplateDTO;

const columnHelper = createColumnHelper<RowItemType>();
const columns = [
    columnHelper.text((row) => row.name, {
        id: 'name',
        label: () => t`Name of the template`,
        size: 160,
    }),
    columnHelper.text((row) => row.description, {
        id: 'description',
        label: () => t`Description`,
        size: 160,
    }),
    columnHelper.enum((row) => row.is_default, {
        id: 'default',
        label: () => t`Default`,
        size: 100,
        getOptionLabel: (option) => (option ? t`True` : t`False`),
        cell: ({ row }) =>
            row.original.is_default ? (
                <Tooltip
                    title={t`This sourcing template is used as the default template for all new sourcing scenarios.`}
                >
                    <Tag label={t`Sourcing`} color="green" attention={'low'} />
                </Tooltip>
            ) : null,
    }),
    columnHelper.date((row) => row.updated_at, {
        id: 'updatedAt',
        size: 100,
        label: () => t`Updated`,
        cell: ({ row }) => (
            <Tooltip title={formatLongDateTime(row.original.updated_at)}>
                <Text variant="inherit" color={colorSystem.neutral[8]}>
                    {formatRelativeTime(row.original.updated_at)}
                </Text>
            </Tooltip>
        ),
    }),
    columnHelper.action({
        id: 'actionMenu',
        size: 80,
        cell: ({ row }) => <RowActionButton sourcingTemplate={row.original} />,
    }),
];

function RowActionButton({ sourcingTemplate }: { sourcingTemplate: SourcingTemplateDTO }): JSX.Element {
    const navigate = useNavigate();

    const { mutateAsync, isPending } = useHttpMutation('DELETE /sourcing-templates/:id', {
        enableInvalidateAllQueries: false,
        snackbarMessage: t`Sourcing template deleted`,
    });

    const { modal, setOpen } = useConfirmationDialog({
        title: t`Delete sourcing template`,
        description: t`Are you sure you want to delete this sourcing template? This cannot be undone.`,
        onConfirmation: () => mutateAsync({ pathParams: { id: sourcingTemplate.id } }),
    });

    const disabled = !useHasPermission(['edit:organization_settings']);

    return (
        <>
            {modal}
            <Flexbox gap={'4px'}>
                <Tooltip title={t`Edit sourcing template`}>
                    <SecondaryIconButton
                        size="small"
                        disabled={disabled}
                        onClick={() =>
                            navigate(
                                route('/sourcing-templates/:sourcingTemplateId/edit', {
                                    sourcingTemplateId: sourcingTemplate.id,
                                }),
                            )
                        }
                    >
                        <EditRounded fontSize="inherit" />
                    </SecondaryIconButton>
                </Tooltip>
                <Tooltip title={t`Copy sourcing template`}>
                    <SecondaryIconButton
                        size="small"
                        disabled={disabled}
                        onClick={() =>
                            navigate(
                                route('/sourcing-templates/:sourcingTemplateId/duplicate', {
                                    sourcingTemplateId: sourcingTemplate.id,
                                }),
                            )
                        }
                    >
                        <ContentCopyRounded fontSize="inherit" />
                    </SecondaryIconButton>
                </Tooltip>

                <Tooltip title={t`Delete sourcing template`}>
                    <DestructiveSecondaryIconButton
                        size="small"
                        disabled={disabled}
                        onClick={() => setOpen(true)}
                        loading={isPending}
                    >
                        <Delete fontSize="inherit" />
                    </DestructiveSecondaryIconButton>
                </Tooltip>
            </Flexbox>
        </>
    );
}

function EmptyPlaceholder(): JSX.Element {
    return (
        <NonIdealState
            Icon={TravelExploreRounded}
            title={t`No sourcing templates found`}
            description={t`This should not happen. Please contact support if you see this.`}
        />
    );
}

function ActionButton() {
    const disabled = !useHasPermission(['edit:organization_settings']);

    if (disabled) {
        return null;
    }

    return (
        <PrimaryButton size={'medium'} href={route('/sourcing-templates/create')}>
            <Trans>Create sourcing template</Trans>
        </PrimaryButton>
    );
}

function InnerContent() {
    const { data } = useSourcingTemplates();
    const hasPermission = useHasPermission(['edit:organization_settings']);

    const { table } = useTanStackTable({
        data,
        columns,
        enableColumnHiding: true,
        enableColumnOrdering: true,
        getRowRoute: hasPermission
            ? (row) => route('/sourcing-templates/:sourcingTemplateId/edit', { sourcingTemplateId: row.original.id })
            : undefined,
    });

    return (
        <Box style={{ height: 'calc(100% - 48px)' }}>
            <TanStackTable table={table} EmptyPlaceholder={EmptyPlaceholder} ActionButton={ActionButton} />
        </Box>
    );
}
export function SourcingTemplatePage() {
    return (
        <PageLayoutCollapsibleSidebar sidebar={<CostingTemplatesSidebar />} layoutVariant="fullWidth">
            <LayoutCard
                title={t`Sourcing`}
                style={{ height: '100%' }}
                description={
                    <Link variant="body" href={getIntercomArticleURL(300759)} rel="noopener noreferrer" target="_blank">
                        Learn more about sourcing templates
                    </Link>
                }
            >
                <InnerContent />
            </LayoutCard>
        </PageLayoutCollapsibleSidebar>
    );
}
